/**
 * Activity Timeline — cloned from `views/roadmap-execution/activity-timeline.ejs` (§20).
 *
 * The screen answers "what has moved since I last looked", grouped project → initiative →
 * item. It is the same `platform.audit_log` the Audit Trail reads, asked a different
 * question (§20.1) — so it shows **Completed**, not `field: status, before: In Progress`.
 *
 * §20.2 — DEFAULT TO EMPTY, AND MEAN IT. No period is selected on open: the screen renders
 * an empty state that asks for one and fetches nothing, so opening it costs a name lookup
 * rather than 20,000 audit rows. The two quick windows and the calendar are ONE control,
 * either/or — choosing a quick window clears the custom range and vice versa, never both
 * lit. A screen that guesses the window shows the user a number they did not ask for, which
 * is worse than showing nothing.
 *
 * §20.7 — NO PREVIEW STEP BETWEEN A BUTTON AND ITS FILE. An export button downloads a file;
 * a modal that first describes what will be in it is an intermediate step nobody asked for.
 * Both buttons post the current window/scope/filters and stream the result straight down.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { pmGet } from '../api';
import './ActivityTimeline.css';

interface Ev {
  id: string;
  at: string;
  actor: string;
  screen: string;
  tbl: string;
  rec: string;
  label: string;
  action: string;
  field: string;
  before: string | null;
  after: string | null;
  dp: string;
  opp: string;
  kind: string;
}

interface KindDef {
  label: string;
  c: string;
  icon: string;
}

/* The twelve kinds, with the colour and icon each carries through the screen, the deck and
   the workbook. Cloned from the source's `kinds` map. */
const KIND: Record<string, KindDef> = {
  completed: { label: 'Completed', c: '#2e7d32', icon: 'task_alt' },
  status: { label: 'Status changed', c: '#1976d2', icon: 'swap_horiz' },
  progress: { label: 'Progress', c: '#0288d1', icon: 'trending_up' },
  date: { label: 'Date moved', c: '#ef6c00', icon: 'event' },
  owner: { label: 'Ownership', c: '#6a1b9a', icon: 'person' },
  created: { label: 'Created', c: '#00897b', icon: 'add_circle_outline' },
  deleted: { label: 'Deleted', c: '#c62828', icon: 'delete_outline' },
  action: { label: 'Follow-up', c: '#3949ab', icon: 'flag' },
  question: { label: 'Question', c: '#00838f', icon: 'help_outline' },
  issue: { label: 'Issue', c: '#c62828', icon: 'error_outline' },
  decision: { label: 'Decision', c: '#8e24aa', icon: 'gavel' },
  other: { label: 'Edited', c: '#78909c', icon: 'edit' },
};

const ACTIVITY_HELP: HelpContent = {
  title: 'Activity Timeline',
  icon: '⏱️',
  purpose:
    'Everything that has <strong>moved</strong> on your projects since you last looked — every status change, completion, date move, ownership change, follow-up, question, issue and decision — grouped <strong>project → initiative → item</strong>, and summarised into a deck you can take to a status meeting.',
  doHere: [
    'Choose a <strong>period</strong> first — <strong>Last 7 days</strong>, <strong>Last 14 days</strong>, or a <strong>Custom range</strong> from the calendar. The screen opens empty on purpose: it never guesses which window you meant.',
    'Use the <strong>scope rail</strong> on the left to narrow to one project, or to a single initiative within it.',
    'Fold a project or initiative band you have already read — the count and the summary chips stay visible while it is closed.',
    'Switch <strong>Group by</strong> to read the same changes by <strong>Day</strong> (what happened Tuesday) or by <strong>Change type</strong> (show me everything that completed).',
    'Press <strong>Generate PowerPoint</strong> to download a deck of exactly the window and scope on screen.',
  ],
  fits: 'Project Management &rarr; Delivery Execution &rarr; <strong>Activity Timeline</strong> &rarr; Execution Control Center',
  firstTime:
    'Press <strong>Last 7 days</strong>. Read the KPI strip, then fold the projects you do not own.',
  sections: [
    {
      label: 'What each change type means',
      type: 'defs',
      items: [
        { term: 'Completed', color: '#2e7d32', def: 'A status moved to Done / Completed / Closed, or progress reached 100%.' },
        { term: 'Status changed', color: '#1976d2', def: 'Any other status move — into In Progress, Waiting or Blocked.' },
        { term: 'Progress', color: '#0288d1', def: 'Percent complete moved without finishing the item.' },
        { term: 'Date moved', color: '#ef6c00', def: 'A delivered-by, due or follow-up date changed — the earliest sign of slippage.' },
        { term: 'Ownership', color: '#6a1b9a', def: 'Owner, assignees or RACI changed.' },
        { term: 'Follow-up / Question / Issue / Decision', color: '#3949ab', def: 'Raised on the Action Register, classified by its type.' },
      ],
      note: 'The type is <em>derived</em> from what actually changed, so the screen answers <strong>what happened</strong> rather than which database column moved.',
    },
    {
      label: 'Where the data comes from',
      type: 'html',
      html: 'Every row is an <strong>audit log</strong> entry — the same record the <strong>Audit Trail</strong> screen shows, asked a different question. Nothing here is entered by hand, and nothing can be edited: it is a read of what the system recorded when someone saved a change.<br><br>You see only the delivery projects you have been given access to, so <strong>the counts are per-viewer</strong> — two people comparing this screen will legitimately see different totals.',
    },
  ],
  links: [
    { label: '→ Execution Control Center', href: '/project_management/program-management/delivery-execution/execution-control-center' },
    { label: '→ Audit Trail (field-level detail)', href: '/project_management/program-management/closeout-audit/audit-trail' },
  ],
};

const PALETTE = ['#90caf9', '#ce93d8', '#a5d6a7', '#ffcc80', '#ef9a9a', '#80cbc4', '#9fa8da', '#ffab91', '#b0bec5'];
function colourFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffff;
  return PALETTE[h % PALETTE.length]!;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

type Group = 'structure' | 'day' | 'kind';

export function ActivityTimeline() {
  const [projects, setProjects] = useState<Record<string, { description: string }>>({});
  const [opps, setOpps] = useState<Record<string, string>>({});
  const [today, setToday] = useState<string>(iso(new Date()));
  const [events, setEvents] = useState<Ev[]>([]);

  const [period, setPeriod] = useState<string | null>(null);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [dFrom, setDFrom] = useState<string>('');
  const [dTo, setDTo] = useState<string>('');
  const [showRange, setShowRange] = useState(false);

  const [kind, setKind] = useState('all');
  const [who, setWho] = useState('all');
  const [scope, setScope] = useState('all');
  const [group, setGroup] = useState<Group>('structure');
  const [closed, setClosed] = useState<Record<string, boolean>>({});

  const [busyPpt, setBusyPpt] = useState(false);
  const [busyCsv, setBusyCsv] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * THE HEIGHT IS MEASURED, NOT TYPED — the same rule as every other screen in this module.
   * `height: 100%` does not work in this shell: `<main>` carries `min-height: 100vh` and
   * grows with its content, so a percentage resolves against nothing and the window scrolls.
   * A callback ref rather than `useRef`, because the effect must re-run when the node
   * actually mounts.
   */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  /* Names only, on open. No events — §20.2. */
  useEffect(() => {
    pmGet<{
      success: boolean;
      projects: Record<string, { description: string }>;
      opps: Record<string, string>;
      today: string;
    }>('/api/activity-timeline/names')
      .then((d) => {
        setProjects(d.projects ?? {});
        setOpps(d.opps ?? {});
        if (d.today) setToday(d.today);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const loadWindow = useCallback((f: string, t: string) => {
    pmGet<{ success: boolean; events: Ev[] }>(
      `/api/activity-timeline?from=${encodeURIComponent(f)}&to=${encodeURIComponent(t)}`,
    )
      .then((d) => {
        setEvents(d.events ?? []);
        setError(null);
      })
      .catch((e: Error) => {
        setEvents([]);
        setError(`Could not load activity: ${e.message}`);
      });
  }, []);

  /* One control, either/or: a quick window clears the calendar (§20.2). */
  const pickQuick = (days: number) => {
    const t = today;
    const f = iso(new Date(new Date(`${today}T00:00:00Z`).getTime() - (days - 1) * 86400000));
    setPeriod(String(days));
    setShowRange(false);
    setFrom(f);
    setTo(t);
    setDFrom(f);
    setDTo(t);
    loadWindow(f, t);
  };

  /* ...and opening the calendar clears the quick window. */
  const pickCustom = () => {
    setPeriod('custom');
    setShowRange(true);
    if (!dFrom) {
      setDFrom(iso(new Date(new Date(`${today}T00:00:00Z`).getTime() - 6 * 86400000)));
      setDTo(today);
    }
  };

  const applyRange = () => {
    if (!dFrom || !dTo) {
      setError('Pick both dates.');
      return;
    }
    if (dFrom > dTo) {
      setError('The From date is after the To date.');
      return;
    }
    setFrom(dFrom);
    setTo(dTo);
    setPeriod('custom');
    loadWindow(dFrom, dTo);
  };

  const reset = () => {
    setPeriod(null);
    setFrom('');
    setTo('');
    setShowRange(false);
    setKind('all');
    setWho('all');
    setScope('all');
    setEvents([]);
    setClosed({});
  };

  const inWindow = useCallback(
    (e: Ev) => (from ? e.at.slice(0, 10) >= from && e.at.slice(0, 10) <= to : false),
    [from, to],
  );

  /* The rail counts ignore scope (you are choosing one) but honour kind and who. */
  const railEvents = useMemo(
    () =>
      events.filter(
        (e) =>
          inWindow(e) && (kind === 'all' || e.kind === kind) && (who === 'all' || e.actor === who),
      ),
    [events, inWindow, kind, who],
  );

  const selected = useMemo(
    () =>
      railEvents.filter((e) => {
        if (scope === 'all') return true;
        const parts = scope.split('|');
        if (parts[0] !== e.dp) return false;
        if (parts.length > 1 && parts[1] !== e.opp) return false;
        return true;
      }),
    [railEvents, scope],
  );

  const people = useMemo(
    () => Array.from(new Set(events.map((e) => e.actor))).sort(),
    [events],
  );

  const fmtWhen = (at: string) => {
    const d = new Date(`${at}:00Z`);
    const end = new Date(`${today}T23:59:59Z`);
    const days = Math.floor((end.getTime() - d.getTime()) / 86400000);
    const hh = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    if (days <= 0) return `today ${hh}`;
    if (days === 1) return `yesterday ${hh}`;
    return `${d.toISOString().slice(5, 10).replace('-', '/')} ${hh}`;
  };

  const dayLabel = (isoDay: string) => {
    const d = new Date(`${isoDay}T12:00:00Z`);
    const diff = Math.round((new Date(`${today}T12:00:00Z`).getTime() - d.getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  };

  const download = async (what: 'deck' | 'export') => {
    const set = what === 'deck' ? setBusyPpt : setBusyCsv;
    set(true);
    setError(null);
    try {
      const r = await fetch(`/api/project_management/activity-timeline/${what}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, to, scope, kind, who }),
      });
      if (!r.ok) {
        const t = await r.text();
        let m = `HTTP ${r.status}`;
        try {
          m = (JSON.parse(t) as { error?: string }).error ?? m;
        } catch {
          /* non-JSON body — keep the status line */
        }
        throw new Error(m);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-timeline-${from}_to_${to}.${what === 'deck' ? 'pptx' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setError(
        `Could not build the ${what === 'deck' ? 'deck' : 'workbook'}: ${(e as Error).message}`,
      );
    } finally {
      set(false);
    }
  };

  const toggleBand = (k: string) =>
    setClosed((c) => {
      const n = { ...c };
      if (n[k]) delete n[k];
      else n[k] = true;
      return n;
    });

  const streamRef = useRef<HTMLDivElement | null>(null);

  // ---------------------------------------------------------------- rail
  const rail = useMemo(() => {
    const byP: Record<string, { n: number; opps: Record<string, number> }> = {};
    railEvents.forEach((e) => {
      const p = (byP[e.dp] = byP[e.dp] ?? { n: 0, opps: {} });
      p.n++;
      if (e.opp) p.opps[e.opp] = (p.opps[e.opp] ?? 0) + 1;
    });
    return byP;
  }, [railEvents]);

  const kpi = (l: string, v: number, c: string) => (
    <div className="kpi" key={l} style={{ ['--k' as string]: c }}>
      <div className="l">{l}</div>
      <div className="v num" style={{ color: c }}>
        {v}
      </div>
    </div>
  );
  const countKind = (k: string) => selected.filter((e) => e.kind === k).length;

  const sumChips = (list: Ev[]) => {
    const c: Record<string, number> = {};
    list.forEach((e) => {
      c[e.kind] = (c[e.kind] ?? 0) + 1;
    });
    return (
      <span className="sum">
        {Object.keys(c)
          .sort((a, b) => (c[b] ?? 0) - (c[a] ?? 0))
          .slice(0, 6)
          .map((k) => {
            const K = KIND[k] ?? KIND.other!;
            return (
              <span key={k} className="sumchip" style={{ color: K.c, background: `${K.c}20` }}>
                {c[k]} {K.label}
              </span>
            );
          })}
      </span>
    );
  };

  const COLS = 6;
  const band = (level: 1 | 2, key: string, colour: string, lbl: string, desc: string, list: Ev[]) => (
    <tr
      key={`band:${key}`}
      className={`band p${level}${closed[key] ? ' closed' : ''}`}
      onClick={() => toggleBand(key)}
    >
      <td colSpan={COLS} style={{ background: `${colour}${level === 1 ? '26' : '14'}`, ['--pc' as string]: colour }}>
        <div className="pb">
          <span className="lbl">
            <span className="material-icons chev">expand_more</span>
            <span style={{ width: 12, height: 12, borderRadius: 4, background: colour }} />
            {lbl}
            <span className="band-n">{list.length}</span>
          </span>
          {desc ? <span className="desc">{desc}</span> : null}
          {sumChips(list)}
        </div>
      </td>
    </tr>
  );

  const valCell = (v: string | null, cls: 'a' | 'b') => {
    if (v === null || v === undefined || v === '') return <span className="val empty">(empty)</span>;
    return (
      <span className={`val ${cls}`} title={v}>
        {v.length > 60 ? `${v.slice(0, 60)}…` : v}
      </span>
    );
  };

  const rows = (list: Ev[], parents: string[]) => {
    const hidden = parents.some((k) => closed[k]);
    if (hidden) return null;
    const byRec: Record<string, Ev[]> = {};
    const order: string[] = [];
    list
      .slice()
      .sort((a, b) => (a.at < b.at ? 1 : -1))
      .forEach((e) => {
        const k = `${e.tbl}:${e.rec}`;
        if (!byRec[k]) {
          byRec[k] = [];
          order.push(k);
        }
        byRec[k]!.push(e);
      });
    return order.flatMap((k) =>
      (byRec[k] ?? []).map((e, i) => {
        const K = KIND[e.kind] ?? KIND.other!;
        return (
          <tr className="crow" key={`${parents.join('~')}~${e.id}`}>
            <td className="item">
              {i === 0 ? (
                <>
                  <div className="nm" title={e.label}>
                    {e.label}
                  </div>
                  <div className="src">
                    <span className="material-icons">
                      {e.tbl === 'action_register' ? 'flag' : 'description'}
                    </span>
                    {e.tbl === 'action_register' ? 'Action Register' : 'Work item'} · {e.screen}
                  </div>
                </>
              ) : (
                <div className="src" style={{ paddingLeft: 2 }}>
                  ↳ same item
                </div>
              )}
            </td>
            <td>
              <span className="kind" style={{ color: K.c, background: `${K.c}18` }}>
                <span className="material-icons">{K.icon}</span>
                {K.label}
              </span>
            </td>
            <td className="fname">{e.field.replace(/_/g, ' ')}</td>
            <td>
              {e.action === 'Created' ? (
                valCell(e.after, 'a')
              ) : (
                <>
                  {valCell(e.before, 'b')} <span style={{ color: '#b0bec5' }}>→</span>{' '}
                  {valCell(e.after, 'a')}
                </>
              )}
            </td>
            <td className="who">{e.actor}</td>
            <td className="when">{fmtWhen(e.at)}</td>
          </tr>
        );
      }),
    );
  };

  const body = () => {
    if (group === 'structure') {
      const byP: Record<string, { list: Ev[]; opps: Record<string, Ev[]> }> = {};
      selected.forEach((e) => {
        const p = (byP[e.dp] = byP[e.dp] ?? { list: [], opps: {} });
        p.list.push(e);
        const ok = e.opp || '—';
        (p.opps[ok] = p.opps[ok] ?? []).push(e);
      });
      return Object.keys(byP)
        .sort()
        .flatMap((pid) => {
          const P = byP[pid]!;
          const out = [band(1, pid, colourFor(pid), pid, projects[pid]?.description ?? '', P.list)];
          Object.keys(P.opps)
            .sort()
            .forEach((oid) => {
              const key = `${pid}|${oid}`;
              const nm = opps[key] ?? (oid === '—' ? 'Project level' : oid);
              out.push(band(2, key, colourFor(key), oid === '—' ? 'Project level' : oid, nm, P.opps[oid]!));
              const r = rows(P.opps[oid]!, [pid, key]);
              if (r) out.push(...r);
            });
          return out;
        });
    }
    if (group === 'day') {
      const byD: Record<string, Ev[]> = {};
      selected.forEach((e) => {
        const d = e.at.slice(0, 10);
        (byD[d] = byD[d] ?? []).push(e);
      });
      return Object.keys(byD)
        .sort()
        .reverse()
        .flatMap((day) => {
          const out = [band(1, `D${day}`, colourFor(day), dayLabel(day), day, byD[day]!)];
          const byP2: Record<string, Ev[]> = {};
          byD[day]!.forEach((e) => {
            (byP2[e.dp] = byP2[e.dp] ?? []).push(e);
          });
          Object.keys(byP2)
            .sort()
            .forEach((pid) => {
              const key = `D${day}|${pid}`;
              out.push(band(2, key, colourFor(pid), pid, projects[pid]?.description ?? '', byP2[pid]!));
              const r = rows(byP2[pid]!, [`D${day}`, key]);
              if (r) out.push(...r);
            });
          return out;
        });
    }
    const byK: Record<string, Ev[]> = {};
    selected.forEach((e) => {
      (byK[e.kind] = byK[e.kind] ?? []).push(e);
    });
    return Object.keys(byK)
      .sort((a, b) => (byK[b]?.length ?? 0) - (byK[a]?.length ?? 0))
      .flatMap((k) => {
        const K = KIND[k] ?? KIND.other!;
        const out = [band(1, `K${k}`, K.c, K.label, '', byK[k]!)];
        const byP3: Record<string, Ev[]> = {};
        byK[k]!.forEach((e) => {
          (byP3[e.dp] = byP3[e.dp] ?? []).push(e);
        });
        Object.keys(byP3)
          .sort()
          .forEach((pid) => {
            const key = `K${k}|${pid}`;
            out.push(band(2, key, colourFor(pid), pid, projects[pid]?.description ?? '', byP3[pid]!));
            const r = rows(byP3[pid]!, [`K${k}`, key]);
            if (r) out.push(...r);
          });
        return out;
      });
  };

  return (
    <div className="at" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <HelpPanel help={ACTIVITY_HELP} />

      <div className="head">
        <div className="head-top">
          <div>
            <h1>Activity Timeline</h1>
            <p>
              Every status, action and edit across your projects — grouped by project, then
              initiative.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 9 }}>
            <button
              type="button"
              className="custom-btn custom-btn-success"
              disabled={!selected.length || busyCsv}
              onClick={() => void download('export')}
            >
              <i className="material-icons">table_view</i>
              {busyCsv ? 'Building…' : 'Export to Excel'}
            </button>
            <button
              type="button"
              className="custom-btn custom-btn-danger"
              disabled={!selected.length || busyPpt}
              onClick={() => void download('deck')}
            >
              <i className="material-icons">slideshow</i>
              {busyPpt ? 'Building deck…' : 'Generate PowerPoint'}
            </button>
          </div>
        </div>

        <div className="filters">
          <div className="ff">
            <label>Period</label>
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-end' }}>
              <div className="seg">
                <button type="button" className={period === '7' ? 'on' : ''} onClick={() => pickQuick(7)}>
                  Last 7 days
                </button>
                <button type="button" className={period === '14' ? 'on' : ''} onClick={() => pickQuick(14)}>
                  Last 14 days
                </button>
                <button type="button" className={period === 'custom' ? 'on' : ''} onClick={pickCustom}>
                  Custom range
                </button>
              </div>
              <div className={`dr${showRange ? ' on' : ''}`}>
                <div className="ff">
                  <label>From</label>
                  <input type="date" max={today} value={dFrom} onChange={(e) => setDFrom(e.target.value)} />
                </div>
                <div className="ff">
                  <label>To</label>
                  <input type="date" max={today} value={dTo} onChange={(e) => setDTo(e.target.value)} />
                </div>
                <button type="button" className="btn ghost" onClick={applyRange}>
                  Apply
                </button>
              </div>
            </div>
          </div>
          <div className="ff">
            <label>Change type</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="all">All changes</option>
              {Object.keys(KIND).map((k) => (
                <option key={k} value={k}>
                  {KIND[k]!.label}
                </option>
              ))}
            </select>
          </div>
          <div className="ff">
            <label>Changed by</label>
            <select value={who} onChange={(e) => setWho(e.target.value)}>
              <option value="all">Anyone</option>
              {people.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="ff">
            <label>&nbsp;</label>
            <button type="button" className="btn ghost" onClick={reset}>
              <span className="material-icons">restart_alt</span>Reset
            </button>
          </div>
        </div>

        {error ? (
          <p style={{ color: '#c62828', fontSize: 13, margin: '10px 0 0' }}>{error}</p>
        ) : null}

        {period ? (
          <div className="kpis">
            {kpi('Changes', selected.length, '#1976d2')}
            {kpi('Completed', countKind('completed'), '#2e7d32')}
            {kpi('Status changes', countKind('status'), '#1976d2')}
            {kpi('Dates moved', countKind('date'), '#ef6c00')}
            {kpi('Follow-ups', countKind('action'), '#3949ab')}
            {kpi('Questions', countKind('question'), '#00838f')}
            {kpi('Issues', countKind('issue'), '#c62828')}
            {kpi('Projects touched', new Set(selected.map((e) => e.dp)).size, '#455a64')}
            {kpi('People', new Set(selected.map((e) => e.actor)).size, '#6a1b9a')}
          </div>
        ) : null}
      </div>

      <div className="body">
        <div className="rail">
          <h4>Scope</h4>
          <button
            type="button"
            className={`scope${scope === 'all' ? ' on' : ''}`}
            onClick={() => setScope('all')}
          >
            <span className="material-icons" style={{ fontSize: 18, color: scope === 'all' ? '#1976d2' : '#b0bec5' }}>
              apps
            </span>
            <span>All my projects</span>
            <span className="n">{railEvents.length}</span>
          </button>
          {Object.keys(rail)
            .sort()
            .map((pid) => {
              const p = rail[pid]!;
              const on = scope === pid;
              return (
                <div key={pid}>
                  <button
                    type="button"
                    className={`scope${on ? ' on' : ''}`}
                    title={projects[pid]?.description ?? ''}
                    onClick={() => setScope(pid)}
                  >
                    <span className="material-icons" style={{ fontSize: 18, color: on ? '#1976d2' : '#b0bec5' }}>
                      folder_open
                    </span>
                    <span className="trunc">{pid}</span>
                    <span className="n">{p.n}</span>
                  </button>
                  {Object.keys(p.opps)
                    .sort()
                    .map((oid) => {
                      const key = `${pid}|${oid}`;
                      const son = scope === key;
                      const nm = opps[key] ?? oid;
                      return (
                        <button
                          type="button"
                          key={key}
                          className={`scope sub${son ? ' on' : ''}`}
                          title={nm}
                          onClick={() => setScope(key)}
                        >
                          <span className="dot" />
                          <span className="trunc">
                            {oid} · {nm.length > 26 ? `${nm.slice(0, 26)}…` : nm}
                          </span>
                          <span className="n">{p.opps[oid]}</span>
                        </button>
                      );
                    })}
                </div>
              );
            })}
        </div>

        <div className="stream" ref={streamRef}>
          {!period ? (
            <div className="empty">
              <span className="material-icons">history</span>
              <h3>Choose a period to see what changed</h3>
              <p>Pick one of the two windows, or set your own dates.</p>
              <div className="quick">
                <button type="button" className="btn primary" onClick={() => pickQuick(7)}>
                  Last 7 days
                </button>
                <button type="button" className="btn ghost" onClick={() => pickQuick(14)}>
                  Last 14 days
                </button>
              </div>
            </div>
          ) : !selected.length ? (
            <div className="empty">
              <span className="material-icons">inbox</span>
              <h3>Nothing changed in this window</h3>
              <p>Widen the period, or clear the change-type and person filters.</p>
            </div>
          ) : (
            <>
              <div className="gbar">
                <span className="lbl">Group by</span>
                <div className="gseg">
                  {(
                    [
                      ['structure', 'Project → Initiative'],
                      ['day', 'Day'],
                      ['kind', 'Change type'],
                    ] as [Group, string][]
                  ).map(([g, lbl]) => (
                    <button
                      type="button"
                      key={g}
                      className={group === g ? 'on' : ''}
                      onClick={() => {
                        setGroup(g);
                        setClosed({});
                      }}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                <button type="button" className="xall" onClick={() => setClosed({})}>
                  Expand all
                </button>
                <button
                  type="button"
                  className="xall"
                  onClick={() => {
                    /* Fold every band the current grouping produces — derived from the data
                       rather than from the DOM, so it does not depend on a paint having
                       happened. The source reads `.band` nodes out of the document; that
                       cannot work here, because React has not rendered them yet when this
                       handler computes the next state. */
                    const all: Record<string, boolean> = {};
                    if (group === 'structure') {
                      selected.forEach((e) => {
                        all[e.dp] = true;
                        all[`${e.dp}|${e.opp || '—'}`] = true;
                      });
                    } else if (group === 'day') {
                      selected.forEach((e) => {
                        all[`D${e.at.slice(0, 10)}`] = true;
                        all[`D${e.at.slice(0, 10)}|${e.dp}`] = true;
                      });
                    } else {
                      selected.forEach((e) => {
                        all[`K${e.kind}`] = true;
                        all[`K${e.kind}|${e.dp}`] = true;
                      });
                    }
                    setClosed(all);
                  }}
                >
                  Collapse all
                </button>
                <span className="gcount">
                  {selected.length} change{selected.length === 1 ? '' : 's'} · {from} to {to}
                </span>
              </div>

              <table className="cm">
                <thead>
                  <tr>
                    <th className="itemh">Item</th>
                    <th>What happened</th>
                    <th>Field</th>
                    <th>Change</th>
                    <th>Who</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>{body()}</tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
